import { useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import IconDownload from '@/assets/svg/icon-export.svg?react';
import IconFilter from '@/assets/svg/icon-filter.svg?react';

import { useIsMobile } from '@/hooks/use-mobile';

import { useAuthStore } from '@/stores/authStore';
import { useFileExplorerStore } from '@/stores/fileExplorerStore';
import { useFiltersStore } from '@/stores/filtersStore';

import { USER_ROLES } from '@/constants/global-constants';
import {
  COLUMN_NAMES,
  ColumnNamesType,
  COLUMNS_BY_TYPE_OF_EXPLORER_ITEM,
  ExplorerItemType,
} from '@/constants/table-columns-config';

import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import FilterBadges from './FilterBadgesComponent';

interface TableActionsComponentProps {
  isSmall: boolean;
  visibleColumns: string[];
  setVisibleColumns: (columns: string[]) => void;
  handleExport?: () => Promise<void>;
  isLoading?: boolean;
  hasMoreItemsAvailable?: boolean;
  entityType: ExplorerItemType;
  currentPage: 'search' | 'dashboard';
}

const TableActionsComponent = ({
  isSmall,
  visibleColumns,
  setVisibleColumns,
  handleExport,
  isLoading = false,
  hasMoreItemsAvailable: hasMoreItemsAvailableLocal,
  entityType,
  currentPage,
}: TableActionsComponentProps) => {
  if (isSmall) {
    return null;
  }

  const isMobile = useIsMobile();
  const { user } = useAuthStore();

  // Calculate if there are more items available beyond what's currently loaded
  const { agreementsState } = useFileExplorerStore();
  const { pagination, setPagination } = useFiltersStore();

  const hasMoreItemsAvailable = useMemo(() => {
    if (hasMoreItemsAvailableLocal !== undefined)
      return hasMoreItemsAvailableLocal;
    if (!pagination) return false;
    const loaded = (pagination.pageIndex + 1) * pagination.pageSize;
    return agreementsState.totalCount > loaded;
  }, [
    hasMoreItemsAvailableLocal,
    agreementsState.totalCount,
    pagination?.pageIndex,
    pagination?.pageSize,
  ]);

  const handleNextPage = () => {
    if (pagination) {
      setPagination({
        pageIndex: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
      });
    }
  };

  const handlePreviousPage = () => {
    if (pagination) {
      setPagination({
        pageIndex: pagination.pageIndex - 1,
        pageSize: pagination.pageSize,
      });
    }
  };

  return (
    <div className="absolute right-6 top-6 z-10 flex items-center justify-end gap-2">
      <FilterBadges currentPage={currentPage} />
      {!isMobile && (
        <>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <IconFilter className="h-6 w-6" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Фільтр полів</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="min-w-[250px] rounded-lg p-2"
            >
              {COLUMNS_BY_TYPE_OF_EXPLORER_ITEM[entityType]
                .filter((columnKey) =>
                  user?.role === USER_ROLES.EXECUTOR.value
                    ? columnKey !== 'insurance_object'
                    : true,
                )
                .map((columnKey) => (
                  <DropdownMenuCheckboxItem
                    key={columnKey}
                    checked={visibleColumns.includes(columnKey)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setVisibleColumns([...visibleColumns, columnKey]);
                      } else {
                        setVisibleColumns(
                          visibleColumns.filter((col) => col !== columnKey),
                        );
                      }
                    }}
                    onSelect={(e) => e.preventDefault()}
                    className="rounded-md hover:bg-neutral-50 data-[state=checked]:bg-transparent"
                  >
                    <div className="flex items-center gap-2">
                      {COLUMN_NAMES(entityType)[columnKey as ColumnNamesType]}
                    </div>
                  </DropdownMenuCheckboxItem>
                ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {handleExport && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" onClick={handleExport}>
                  <IconDownload className="h-6 w-6" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Експорт таблиці</p>
              </TooltipContent>
            </Tooltip>
          )}
        </>
      )}
      {!isSmall && (
        <div className="flex items-center justify-center px-3 py-1.5">
          <div className="relative flex h-[22px] w-[22px] items-center justify-center rounded-[4px] border border-primary sm:h-[26px] sm:w-[26px]">
            <button
              onClick={handlePreviousPage}
              disabled={isLoading || !pagination?.pageIndex}
              className={`absolute right-full top-1/2 -translate-y-1/2 transition-opacity ${isLoading ? 'opacity-50' : ''}`}
            >
              <ChevronLeft className="h-[14px] w-[14px] sm:h-4 sm:w-4" />
            </button>
            <button
              onClick={handleNextPage}
              disabled={isLoading || !hasMoreItemsAvailable}
              className={`absolute left-full top-1/2 -translate-y-1/2 transition-opacity ${isLoading ? 'opacity-50' : ''}`}
            >
              <ChevronRight className="h-[14px] w-[14px] sm:h-4 sm:w-4" />
            </button>
            <span>{pagination?.pageIndex! + 1 || 1}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableActionsComponent;
